<script setup lang="ts">
import Chat from '@/Components/Chat.vue';
import ScrollView from '@/Components/ScrollView.vue';
import { Phase, Round, Session } from '@/objects';
import { scrollToLastNext } from '@/utils';
import { computed, useTemplateRef } from 'vue';

const {
    session
} = defineProps({
    session: {
        type: Session
    },
    flipChat: {
        type: Boolean,
        default: false
    }
})

const scrollContent = useTemplateRef('scroll-content')

// Items to render in the chat history. When playing phase 2, the phase 1 rounds are
// shown above as read-only history, followed by a summary of the inter-phase vote, then
// the phase 2 rounds. Round numbers continue across both phases (so phase 2 doesn't
// restart at "Round 1"); the vote summary doesn't consume a round number. Each round
// carries its own phase ruleset for score display.
const rounds = computed(() => {
    const out = []

    const parsePhase = (ph: Phase) => {
        if (!ph) {
            return
        }

        ph.rounds.forEach((round, idx) => {
            out.push({
                type: 'round',
                round,
                ruleset: ph.ruleset,
                number: idx,
                phaseIdx: ph.idx,
                historic: ph !== session.currentPhase,
            })
        })
    }

    parsePhase(session.phase1)

    if (session.voteDone) {
        out.push({
            type: 'vote',
            voted: session.votedRuleset,
            won: session.phase2.ruleset,
        })

        parsePhase(session.phase2)
    }

    return out
})

// Split a round's chat into chunks of delivered messages interleaved with the privacy-
// filter rejections that happened between them, so each rejected attempt shows in its
// original position. Rejections always occur on the participant's turn (an even number of
// delivered entries precede them), so every chunk still starts on a participant message —
// which keeps <Chat>'s left/right alternation correct.
function roundSegments(round: Round) {
    const blocked = round.blockedMessages ?? []
    if (blocked.length === 0) {
        return [{ type: 'chat', msgs: round.messages }]
    }

    const segments = []
    const anchors = Array.from(new Set(blocked.map((b) => b.after))).sort((a, b) => a - b)
    let cursor = 0

    for (const anchor of anchors) {
        if (anchor > cursor) {
            segments.push({ type: 'chat', msgs: round.messages.slice(cursor, anchor) })
            cursor = anchor
        }
        for (const b of blocked.filter((x) => x.after === anchor)) {
            segments.push({ type: 'blocked', body: b.body })
        }
    }

    if (cursor < round.messages.length) {
        segments.push({ type: 'chat', msgs: round.messages.slice(cursor) })
    }

    return segments
}

function scrollToLast() {
    scrollToLastNext(scrollContent.value)
}

defineExpose({
    scrollToLast
})
</script>

<template>
    <ScrollView class="flex-1" :inner-classes="'col-rev'">
        <div class="col gap-3" ref="scroll-content">
            <slot name="before"></slot>
            <template v-for="(item, i) in rounds" :key="i">
                <!-- Inter-phase vote summary: the table you voted vs the one chosen for your group -->
                <slot name="vote" v-if="item.type === 'vote'" :item="item"></slot>
                <!-- A played or playable round -->
                <div v-else class="col w-full items-center gap-3">
                    <div class="row bg-(--bg-color) items-center sticky top-0 w-full gap-2">
                        <span class="bg-pink-900 text-white text-xs text-center px-2 py-1 rounded-sm">
                            Fase {{ item.phaseIdx }} - Round {{ item.number+1 }}
                        </span>
                        <hr class="border flex-1 border-gray-900">
                    </div>
                    <template v-for="(seg, si) in roundSegments(item.round)" :key="si">
                        <Chat v-if="seg.type === 'chat' && seg.msgs.length" class="w-full gap-3" :messages="seg.msgs" :first-left="flipChat"/>
                        <!-- A message the privacy filter rejected: shown to its author, never delivered -->
                        <div v-else-if="seg.type === 'blocked'" class="blocked-msg ml-auto col items-end gap-1 max-w-[80%]">
                            <div class="bg-error/10 border border-error rounded-t-[8px] rounded-bl-[8px] px-4 py-2 text-right wrap-break-word line-through opacity-70">
                                {{ seg.body }}
                            </div>
                            <div class="text-xs text-error text-right">
                                Messaggio non inviato: contiene dati personali. Riprova senza informazioni personali.
                            </div>
                        </div>
                    </template>
                    <slot name="waiting" :item="item"></slot>
                    <slot name="choices" v-if="item.round.choices[0]" :item="item"></slot>
                </div>
            </template>
            <slot name="after"></slot>
        </div>
    </ScrollView>
</template>

<style scoped>
</style>